/**
 * CascadeEditor Component
 *
 * Cascade risk editor for a single risk profile. Displays:
 * - Account default risk in the card header (value input + %/$ toggle)
 * - Strategy & playbook overrides in the card body (strategy tabs,
 *   override toggles, and inheritance display)
 *
 * All mutations produce a full updated RiskProfile emitted via onUpdateProfile.
 */

// 1. External dependencies
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { Eye, EyeOff } from 'lucide-react';

// 2. Workspace packages
import {
  Button,
  ButtonGroup,
  Card,
  CardHeader,
  CardBody,
  Chip,
  Input,
  Link,
  Switch,
  Tab,
  Tabs,
} from '@enfomentis/ui';

// 3. Internal absolute imports
import type { RiskProfile, RiskSpec } from '@/types/schema/AccountRisk';
import type { Strategy } from '@/types/schema/Strategy';
import type { Playbook } from '@/types/schema/Playbook';
import { isEntityActive } from '@/types/shared/EntityStatus';

// 4. Relative imports
import { formatRisk } from '@/utils/riskDisplayHelpers';
import { toDisplayValue, toStoredValue, defaultValueForType } from './riskHelpers';

// =============================================================================
// Types
// =============================================================================

interface CascadeEditorProps {
  profile: RiskProfile;
  strategies: Strategy[];
  strategyPlaybooks: Map<string, Playbook[]>;
  onUpdateProfile: (updatedProfile: RiskProfile) => void;
  currency: string;
}

/**
 * Resolve initial selected strategy: first strategy with an override in this
 * profile, else first strategy, else null.
 */
function resolveStrategyId(
  profile: RiskProfile,
  strategies: Strategy[]
): string | null {
  if (strategies.length === 0) return null;
  const withOverride = strategies.find(s =>
    profile.strategyOverrides?.some(o => o.strategyId === s.id)
  );
  if (withOverride) return withOverride.id;
  return strategies[0]?.id ?? null;
}

// =============================================================================
// Component
// =============================================================================

export const CascadeEditor: React.FC<CascadeEditorProps> = ({
  profile,
  strategies,
  strategyPlaybooks,
  onUpdateProfile,
  currency,
}) => {
  const navigate = useNavigate();

  // ---------------------------------------------------------------------------
  // Local UI state: which strategy is selected in the overrides section
  // ---------------------------------------------------------------------------
  const [selectedStrategyId, setSelectedStrategyId] = useState<string | null>(
    () => resolveStrategyId(profile, strategies)
  );
  const [showArchivedPlaybooks, setShowArchivedPlaybooks] = useState(false);

  // Track previous profile.id to force-reset on profile switch
  const prevProfileIdRef = useRef(profile.id);

  // Revalidate strategy selection when profile or strategy set changes.
  // Force-reset on profile switch; only revalidate on strategy-set change.
  const strategyIds = strategies.map(s => s.id).join(',');

  useEffect(() => {
    const profileChanged = prevProfileIdRef.current !== profile.id;
    prevProfileIdRef.current = profile.id;

    if (profileChanged || !selectedStrategyId || !strategies.some(s => s.id === selectedStrategyId)) {
      setSelectedStrategyId(resolveStrategyId(profile, strategies));
    }
    // Intentionally uses derived primitives, not full objects
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.id, strategyIds]);

  // Reset archived playbook toggle when strategy or profile changes
  useEffect(() => {
    setShowArchivedPlaybooks(false);
  }, [selectedStrategyId, profile.id]);

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------
  const selectedStrategy = strategies.find(s => s.id === selectedStrategyId);
  const selectedPlaybooks = selectedStrategyId
    ? strategyPlaybooks.get(selectedStrategyId) ?? []
    : [];
  const hasArchivedPlaybooks = selectedPlaybooks.some(p => p.status === 'archived');
  const visiblePlaybooks = selectedPlaybooks.filter(
    p => isEntityActive(p.status) || (showArchivedPlaybooks && p.status === 'archived')
  );

  const strategyOverride = selectedStrategyId
    ? profile.strategyOverrides?.find(o => o.strategyId === selectedStrategyId)
    : undefined;

  // ---------------------------------------------------------------------------
  // Cascade resolution
  // ---------------------------------------------------------------------------

  /** Get the effective risk for a playbook (playbook override → strategy override → account default) */
  const getPlaybookEffectiveRisk = (playbookId: string): RiskSpec => {
    const playbookOverride = profile.playbookOverrides?.find(
      o => o.playbookId === playbookId
    );
    if (playbookOverride) return playbookOverride.risk;
    if (strategyOverride) return strategyOverride.risk;
    return profile.accountDefault;
  };

  /** Get the parent risk a playbook would inherit from */
  const getPlaybookParentRisk = (): RiskSpec => {
    if (strategyOverride) return strategyOverride.risk;
    return profile.accountDefault;
  };

  // ---------------------------------------------------------------------------
  // Mutation handlers
  // ---------------------------------------------------------------------------

  const handleToggleStrategyOverride = (
    strategyId: string,
    enabled: boolean
  ): void => {
    if (enabled) {
      const newOverride = {
        strategyId,
        risk: { ...profile.accountDefault },
      };
      onUpdateProfile({
        ...profile,
        strategyOverrides: [...(profile.strategyOverrides || []), newOverride],
      });
    } else {
      const filtered = (profile.strategyOverrides || []).filter(
        o => o.strategyId !== strategyId
      );
      if (filtered.length > 0) {
        onUpdateProfile({ ...profile, strategyOverrides: filtered });
      } else {
        // Remove the optional key entirely (exactOptionalPropertyTypes)
        const { strategyOverrides: _, ...rest } = profile;
        onUpdateProfile(rest as RiskProfile);
      }
    }
  };

  const handleUpdateStrategyRisk = (
    strategyId: string,
    risk: RiskSpec
  ): void => {
    const updated = (profile.strategyOverrides || []).map(o =>
      o.strategyId === strategyId ? { ...o, risk } : o
    );
    onUpdateProfile({ ...profile, strategyOverrides: updated });
  };

  const handleTogglePlaybookOverride = (
    playbookId: string,
    enabled: boolean
  ): void => {
    if (enabled) {
      const parentRisk = getPlaybookParentRisk();
      const newOverride = {
        playbookId,
        risk: { ...parentRisk },
      };
      onUpdateProfile({
        ...profile,
        playbookOverrides: [...(profile.playbookOverrides || []), newOverride],
      });
    } else {
      const filtered = (profile.playbookOverrides || []).filter(
        o => o.playbookId !== playbookId
      );
      if (filtered.length > 0) {
        onUpdateProfile({ ...profile, playbookOverrides: filtered });
      } else {
        // Remove the optional key entirely (exactOptionalPropertyTypes)
        const { playbookOverrides: _, ...rest } = profile;
        onUpdateProfile(rest as RiskProfile);
      }
    }
  };

  const handleUpdatePlaybookRisk = (
    playbookId: string,
    risk: RiskSpec
  ): void => {
    const updated = (profile.playbookOverrides || []).map(o =>
      o.playbookId === playbookId ? { ...o, risk } : o
    );
    onUpdateProfile({ ...profile, playbookOverrides: updated });
  };

  // ---------------------------------------------------------------------------
  // Shared input change handler
  // ---------------------------------------------------------------------------

  const handleRiskInputChange = (
    currentRisk: RiskSpec,
    rawValue: string,
    onUpdate: (risk: RiskSpec) => void
  ): void => {
    const inputValue = rawValue === '' ? 0 : parseFloat(rawValue);
    if (!isNaN(inputValue)) {
      onUpdate({
        ...currentRisk,
        value: toStoredValue(inputValue, currentRisk.type),
      });
    }
  };

  const handleTypeToggle = (
    isFixed: boolean,
    onUpdate: (risk: RiskSpec) => void
  ): void => {
    const newType = isFixed ? 'fixed' : 'percentage';
    onUpdate({ type: newType, value: defaultValueForType(newType) });
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  /** Render strategy override controls + "Playbooks" label (pinned in CardHeader) */
  const renderStrategyHeader = (): React.ReactNode => {
    if (!selectedStrategy) return null;

    return (
      <div className="space-y-3">
        {/* Strategy override section */}
        <div className="p-3 bg-default-50 rounded-lg space-y-3">
          {/* Show strategy name label when only 1 strategy (no tabs) */}
          {strategies.length === 1 && (
            <div className="text-base">
              {selectedStrategy.name}
            </div>
          )}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <Switch
              isSelected={!!strategyOverride}
              onValueChange={(enabled) => {
                handleToggleStrategyOverride(selectedStrategy.id, enabled);
              }}
              size="sm"
            >
              <span className="text-sm">Override Account Risk</span>
            </Switch>

            <div className="flex items-center gap-3 sm:ml-auto">
              {strategyOverride ? (
                <>
                  <Input
                    type="number"
                    step="any"
                    label="Strategy Risk"
                    labelPlacement="outside-left"
                    value={toDisplayValue(strategyOverride.risk)}
                    onChange={(e) => {
                      handleRiskInputChange(
                        strategyOverride.risk,
                        e.target.value,
                        (risk) => handleUpdateStrategyRisk(selectedStrategy.id, risk)
                      );
                    }}
                    min="0"
                    {...(strategyOverride.risk.type === 'percentage' ? { max: '100' } : {})}
                    classNames={{
                      label: 'text-sm',
                      input: 'text-right mr-2',
                    }}
                    size="sm"
                  />

                  <ButtonGroup
                    options={[
                      { value: 'percentage', label: '%' },
                      { value: 'fixed', label: currency },
                    ]}
                    value={strategyOverride.risk.type}
                    onChange={(val) => {
                      handleTypeToggle(val === 'fixed', (risk) =>
                        handleUpdateStrategyRisk(selectedStrategy.id, risk)
                      );
                    }}
                    size="sm"
                    variant="flat"
                  />
                </>
              ) : (
                <div className="text-sm text-text-secondary italic">
                  Inherits from account default: {formatRisk(profile.accountDefault, currency)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Playbooks column headers */}
        {(visiblePlaybooks.length > 0 || hasArchivedPlaybooks) && (
          <div className="grid grid-cols-[1fr_5.5rem_19rem] gap-3 px-1 text-xs font-semibold text-text-secondary uppercase tracking-wide">
            <div className="flex items-center gap-2">
              <span>Playbooks</span>
              {hasArchivedPlaybooks && (
                <Button
                  size="sm"
                  variant="light"
                  startContent={showArchivedPlaybooks ? <Eye size={14} /> : <EyeOff size={14} />}
                  onPress={() => setShowArchivedPlaybooks(!showArchivedPlaybooks)}
                  title={showArchivedPlaybooks ? 'Hide Archived' : 'Show Archived'}
                  aria-label={showArchivedPlaybooks ? 'Hide Archived' : 'Show Archived'}
                  className={showArchivedPlaybooks ? 'text-text-primary' : 'text-text-tertiary'}
                >
                  Archived
                </Button>
              )}
            </div>
            <span>Override</span>
            <span>Risk</span>
          </div>
        )}
      </div>
    );
  };

  /** Render playbook override items (scrollable in CardBody) */
  const renderPlaybookList = (): React.ReactNode => {
    if (!selectedStrategy || visiblePlaybooks.length === 0) return null;

    return (
      <div className="space-y-1">
        {visiblePlaybooks.map((playbook) => {
          const playbookOverride = profile.playbookOverrides?.find(
            o => o.playbookId === playbook.id
          );
          const effectiveRisk = getPlaybookEffectiveRisk(playbook.id);
          const parentLabel = strategyOverride ? 'strategy' : 'account default';
          const isArchived = playbook.status === 'archived';

          return (
            <div key={playbook.id} className="grid grid-cols-[1fr_5rem_18rem] gap-3 items-center py-1.5 px-1">
              <div className="flex items-center gap-2 min-w-0">
                <span className={`text-sm font-medium truncate min-w-0${isArchived ? ' text-text-secondary' : ''}`}>
                  {playbook.name}
                </span>
                {isArchived && (
                  <Chip size="sm" variant="flat" color="default" className="shrink-0">Archived</Chip>
                )}
              </div>

              <div className="flex justify-start">
                <Switch
                  isSelected={!!playbookOverride}
                  onValueChange={(enabled) => {
                    handleTogglePlaybookOverride(playbook.id, enabled);
                  }}
                  size="sm"
                  aria-label={`Override ${playbook.name}`}
                />
              </div>

              <div className="flex items-center gap-3">
                {playbookOverride ? (
                  <>
                    <Input
                      type="number"
                      step="any"
                      value={toDisplayValue(playbookOverride.risk)}
                      onChange={(e) => {
                        handleRiskInputChange(
                          playbookOverride.risk,
                          e.target.value,
                          (risk) => handleUpdatePlaybookRisk(playbook.id, risk)
                        );
                      }}
                      min="0"
                      {...(playbookOverride.risk.type === 'percentage' ? { max: '100' } : {})}
                      classNames={{
                        input: 'text-right mr-2',
                      }}
                      size="sm"
                    />

                    <ButtonGroup
                      options={[
                        { value: 'percentage', label: '%' },
                        { value: 'fixed', label: currency },
                      ]}
                      value={playbookOverride.risk.type}
                      onChange={(val) => {
                        handleTypeToggle(val === 'fixed', (risk) =>
                          handleUpdatePlaybookRisk(playbook.id, risk)
                        );
                      }}
                      size="sm"
                      variant="flat"
                    />
                  </>
                ) : (
                  <div className="text-sm text-text-secondary italic">
                    Inherits from {parentLabel}:{' '}
                    {formatRisk(effectiveRisk, currency)}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Card radius="none" shadow="none" className="flex-1 min-h-0 flex flex-col p-1 pt-2">
      {/* ================================================================= */}
      {/* CardHeader: Strategy Tabs + Override Controls (pinned)            */}
      {/* ================================================================= */}
      <CardHeader className="px-1 pt-0 pb-2 min-h-10 flex flex-col items-stretch gap-3 text-sm text-text-primary border-b-1 border-default-200">
        {/* ---- Strategy tabs (only when >1 strategy) ---- */}
        {strategies.length > 1 && (
          <Tabs
            aria-label="Strategy selector"
            selectedKey={selectedStrategyId || ''}
            onSelectionChange={(key) => {
              const val = String(key);
              if (val) setSelectedStrategyId(val);
            }}
            variant="underlined"
            color="primary"
            size="md"
          >
            {strategies.map((strategy) => {
              const hasOverride = profile.strategyOverrides?.some(
                o => o.strategyId === strategy.id
              );
              return (
                <Tab
                  key={strategy.id}
                  title={
                    <div className="flex items-center gap-2">
                      {hasOverride && (
                        <div className="w-2 h-2 rounded-full bg-primary shrink-0" />
                      )}
                      <span>{strategy.name}</span>
                    </div>
                  }
                />
              );
            })}
          </Tabs>
        )}

        {/* ---- Strategy override controls + "Playbooks" label (pinned) ---- */}
        {selectedStrategy && renderStrategyHeader()}
      </CardHeader>

      {/* ================================================================= */}
      {/* CardBody: Playbook Overrides (scrolls)                            */}
      {/* ================================================================= */}
      <CardBody className="flex-1 min-h-0 overflow-y-auto px-1 pb-1 space-y-3">
        {strategies.length === 0 ? (
          /* ---- Empty state: no strategies ---- */
          <div className="py-4 text-center">
            <p className="text-sm text-text-tertiary mb-2">
              No strategies configured yet.
            </p>
            <Link
              size="sm"
              onPress={() => navigate('/settings/strategies')}
              className="cursor-pointer"
            >
              Go to Trading Strategies
            </Link>
          </div>
        ) : (
          /* ---- Playbook list (scrollable) ---- */
          renderPlaybookList()
        )}
      </CardBody>
    </Card>
  );
};
